import React from "react";
import { motion } from "framer-motion";
import { contactData } from "../data/mock";
import {
  Phone,
  MessageCircle,
  MapPin,
  Clock,
  ArrowRight,
  ExternalLink,
} from "lucide-react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";

const ContactSection = () => {
  const handleCall = () => window.open(`tel:${contactData.phone}`, "_self");

  const handleWhatsApp = () => {
    const message = encodeURIComponent(
      "Hello Bahubali Cabs! I would like to book a taxi in Udaipur. Please share the rates.",
    );
    window.open(
      `https://wa.me/${contactData.whatsapp}?text=${message}`,
      "_blank",
    );
  };

  return (
    <section
      id="contact"
      className="py-24 bg-slate-50 relative overflow-hidden"
    >
      {/* 🔹 Decorative Elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-amber-100/30 rounded-full blur-[120px] translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-amber-200/20 rounded-full blur-[100px] -translate-x-1/2 translate-y-1/2" />

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-amber-600 font-black uppercase tracking-[0.3em] text-xs"
          >
            Connect With Us
          </motion.span>
          <h2 className="text-4xl md:text-6xl font-black text-slate-900 mt-2 tracking-tighter">
            Book Your <span className="text-amber-500 italic">Ride</span>
          </h2>
          <div className="w-20 h-1.5 bg-amber-500 mx-auto mt-6 rounded-full" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-stretch">
          {/* 🔹 Contact Info Column */}
          <div className="space-y-6">
            {/* Call Card */}
            <ContactCard
              icon={<Phone className="w-7 h-7" />}
              title="Quick Call"
              value={contactData.phone}
              desc="Instant booking & inquiry"
              btnText="Call Now"
              btnColor="bg-slate-900 text-white"
              onClick={handleCall}
            />

            {/* WhatsApp Card */}
            <ContactCard
              icon={<MessageCircle className="w-7 h-7" />}
              title="WhatsApp Chat"
              value={contactData.whatsapp}
              desc="Get rates & car photos"
              btnText="Chat on WA"
              btnColor="bg-emerald-600 text-white"
              onClick={handleWhatsApp}
            />

            {/* Info Box (Location & Time) */}
            <Card className="bg-white p-8 rounded-[2rem] border-none shadow-2xl shadow-slate-200/50 space-y-8">
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center shrink-0">
                  <MapPin className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h4 className="font-black text-slate-900 uppercase text-sm tracking-widest">
                    Office Address
                  </h4>
                  <p className="text-slate-500 font-medium mt-1 leading-relaxed">
                    Shiv Colony, Pratap Nagar, Near Old RTO, Udaipur, Rajasthan
                    313001
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-5">
                <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center shrink-0">
                  <Clock className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h4 className="font-black text-slate-900 uppercase text-sm tracking-widest">
                    Service Hours
                  </h4>
                  <p className="text-slate-500 font-medium mt-1">
                    Open 24 Hours / 7 Days a Week
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* 🔹 Map Column */}
          <Card className="relative rounded-[2.5rem] overflow-hidden border-none shadow-2xl min-h-[500px] group">
            <iframe
              title="Bahubali Cabs Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3628.232537554556!2d73.72591637536302!3d24.581177678111957!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3967e54f97970d47%3A0x6a6d6d6d6d6d6d6d!2sPratap%20Nagar%2C%20Udaipur%2C%20Rajasthan!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
              width="100%"
              height="100%"
              className="absolute inset-0 grayscale-[0.2] contrast-[1.1]"
              loading="lazy"
              allowFullScreen
            />
            <div className="absolute bottom-6 left-6 right-6">
              <Button
                onClick={() => window.open("https://maps.google.com", "_blank")}
                className="w-full bg-white/90 backdrop-blur text-slate-900 font-black rounded-2xl py-6 shadow-xl hover:bg-white"
              >
                <ExternalLink className="w-4 h-4 mr-2" /> Open In Google Maps
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

/* Reusable Contact Card for Audit Cleanliness */
const ContactCard = ({
  icon,
  title,
  value,
  desc,
  btnText,
  btnColor,
  onClick,
}) => (
  <Card className="group bg-white p-6 rounded-[2rem] border-none shadow-xl shadow-slate-200/50 hover:shadow-2xl transition-all duration-300">
    <div className="flex items-center gap-6">
      <div
        className={`w-16 h-16 ${btnColor} rounded-2xl flex items-center justify-center shadow-lg transition-transform group-hover:rotate-6`}
      >
        {icon}
      </div>
      <div className="flex-1">
        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
          {title}
        </h4>
        <p className="text-xl font-black text-slate-900 mt-0.5">{value}</p>
        <p className="text-xs text-slate-500 font-medium">{desc}</p>
      </div>
      <Button
        onClick={onClick}
        className={`${btnColor} rounded-xl px-6 font-bold text-xs h-11 hidden md:flex`}
      >
        {btnText} <ArrowRight className="w-3 h-3 ml-2" />
      </Button>
    </div>
    {/* Mobile Only Button */}
    <Button
      onClick={onClick}
      className={`w-full mt-4 md:hidden ${btnColor} rounded-xl font-bold py-6`}
    >
      {btnText}
    </Button>
  </Card>
);

export default ContactSection;
